import { QueryClient, type QueryFunction } from "@tanstack/react-query";

const DEFAULT_QUERY_STALE_TIME_MS = 60_000;
const DEFAULT_QUERY_GC_TIME_MS = 5 * 60_000;
async function throwIfResNotOk(res: Response) {
  if (!res.ok) {
    const text = (await res.text()) || res.statusText;
    throw new Error(`${res.status}: ${text}`);
  }
}
export async function apiRequest<T = any>(
  method: string,
  url: string,
  data?: unknown,
  headers?: Record<string, string>
): Promise<T> {
  const res = await fetch(url, {
    method,
    headers: {
      ...(data ? { "Content-Type": "application/json" } : {}),
      ...headers,
    },
    body: data ? JSON.stringify(data) : undefined,
    credentials: "include",
  });
  await throwIfResNotOk(res);
  return (await res.json()) as T;
}
type UnauthorizedBehavior = "returnNull" | "throw";
export const getQueryFn =
  <T>(options: { on401: UnauthorizedBehavior }): QueryFunction<T> =>
  async ({ queryKey }) => {
    const res = await fetch(queryKey.join("/") as string, {
      credentials: "include",
    });
    if (options.on401 === "returnNull" && res.status === 401) {
      return null as T;
    }
    await throwIfResNotOk(res);
    return (await res.json()) as T;
  };
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      queryFn: getQueryFn({ on401: "throw" }),
      refetchInterval: false,
      refetchOnWindowFocus: false,
      staleTime: DEFAULT_QUERY_STALE_TIME_MS,
      gcTime: DEFAULT_QUERY_GC_TIME_MS,
      retry: false,
    },
    mutations: { retry: false },
  },
});
